// src/components/Codebook/CodebookViewer.tsx
// Content display and navigation for codebooks

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CodebookGetResponse, isNarrativeBlock, isCodeBlock, isCodeDualBlock, isNextTopicsBlock } from '../../types/chat';
import codebookApi from '../../services/codebookApi';
import CodebookNarrativeBlock from './CodebookNarrativeBlock';
import CodebookCodeBlock from './CodebookCodeBlock';
import CodebookDualCodeBlock from './CodebookDualCodeBlock';
import DocumentationButton from '../Documentation/DocumentationButton';
import DocumentationModal from '../Documentation/DocumentationModal';
import { useAuth } from '../../contexts/AuthContext';
import { PathLaunchContext } from '../../stores/codebookStore';

interface CodebookViewerProps {
  codebook: CodebookGetResponse;
  onBack: () => void;
  showPrompt?: boolean;
  onGenerateNext?: (prompt: string) => void;
  // When the tutorial is a Learning Path step, the ending sends the student
  // back to their path instead of offering Bookmate's own follow-up tutorials
  // (two competing "what next" navigators would be misleading).
  pathContext?: PathLaunchContext | null;
}

const CodebookViewer: React.FC<CodebookViewerProps> = ({ codebook, onBack, showPrompt = true, onGenerateNext, pathContext = null }) => {
  const navigate = useNavigate();
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [isShared, setIsShared] = useState(codebook.is_public ?? false);
  const [shareNotification, setShareNotification] = useState<string | null>(null);
  const [isShareLoading, setIsShareLoading] = useState(false);
  const [documentationOpen, setDocumentationOpen] = useState(false);
  const { user } = useAuth();
  // v3.19.7: Starter tier — no PDF download, no public share. Pro/Ultimate unlocked.
  const isStarter = user?.subscription_tier === 'starter';

  // While the backend is still streaming blocks, render in read-only mode and
  // append a "writing next section…" placeholder under the last block.
  const isStreaming = codebook.status === 'generating';

  const handleShare = async () => {
    setIsShareLoading(true);
    try {
      if (!isShared) {
        // Enable sharing first
        await codebookApi.toggleShare(codebook.codebook_id, true);
        setIsShared(true);
      }
      // Copy link to clipboard
      const shareUrl = `${window.location.origin}/bookmate/shared/${codebook.codebook_id}`;
      await navigator.clipboard.writeText(shareUrl);
      setShareNotification('Link copied!');
      setTimeout(() => setShareNotification(null), 2000);
    } catch (err: any) {
      console.error('[CodebookViewer] Share error:', err);
      setShareNotification('Failed to share');
      setTimeout(() => setShareNotification(null), 2000);
    } finally {
      setIsShareLoading(false);
    }
  };

  const handleUnshare = async () => {
    setIsShareLoading(true);
    try {
      await codebookApi.toggleShare(codebook.codebook_id, false);
      setIsShared(false);
      setShareNotification('Sharing disabled');
      setTimeout(() => setShareNotification(null), 2000);
    } catch (err: any) {
      console.error('[CodebookViewer] Unshare error:', err);
      setShareNotification('Failed to unshare');
      setTimeout(() => setShareNotification(null), 2000);
    } finally {
      setIsShareLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setIsPdfGenerating(true);
    setPdfError(null);

    try {
      const pdfBlob = await codebookApi.downloadPDF(codebook.codebook_id);

      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${codebook.title.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      console.error('[CodebookViewer] PDF download error:', err);
      setPdfError(err.response?.data?.detail || 'Failed to generate PDF');
    } finally {
      setIsPdfGenerating(false);
    }
  };

  // Handler for documentation prompt clicks
  const handleDocumentationPromptClick = (prompt: string) => {
    if (onGenerateNext) {
      onGenerateNext(prompt);
    }
  };

  return (
    <div className="codebook-viewer h-full flex flex-col">
      {/* Header with navigation */}
      <div className="flex-shrink-0 border-b border-border bg-background/95 backdrop-blur-sm sticky top-0 z-10">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-card rounded-lg transition-colors text-muted-foreground hover:text-foreground"
              title="Back"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div>
              <h2 className="text-xl font-bold text-foreground">{codebook.title}</h2>
              {isStreaming ? (
                <p className="text-xs text-primary mt-1 flex items-center gap-2">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary animate-pulse"></span>
                  Writing tutorial · {codebook.total_blocks} section{codebook.total_blocks !== 1 ? 's' : ''} so far
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">
                  {codebook.total_blocks} blocks • {codebook.code_blocks} code examples
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {pdfError && (
              <div className="text-xs text-red-500 mr-2">
                {pdfError}
              </div>
            )}
            {shareNotification && (
              <div className="text-xs text-green-600 dark:text-green-400 mr-2 font-medium">
                {shareNotification}
              </div>
            )}

            <DocumentationButton onClick={() => setDocumentationOpen(true)} />

            {/* Share / Copy Link / Unshare — Pro/Ultimate only (v3.19.7) */}
            {codebook.status === 'completed' && !isStarter && (
              <>
                {isShared && (
                  <button
                    onClick={handleUnshare}
                    disabled={isShareLoading}
                    className="px-3 py-2 text-sm border border-border hover:bg-card disabled:opacity-50 rounded-lg transition-colors text-muted-foreground hover:text-foreground"
                    title="Disable public sharing"
                  >
                    Unshare
                  </button>
                )}
                <button
                  onClick={handleShare}
                  disabled={isShareLoading}
                  className="px-4 py-2 border border-border hover:bg-card disabled:opacity-50 disabled:cursor-not-allowed font-medium rounded-lg transition-colors flex items-center gap-2 text-foreground"
                >
                  {isShareLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent"></div>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                  )}
                  {isShared ? 'Copy Link' : 'Share'}
                </button>
              </>
            )}

            {!isStreaming && !isStarter && (
              <button
                onClick={handleDownloadPDF}
                disabled={isPdfGenerating}
                className="px-4 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center gap-2"
              >
                  {isPdfGenerating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download PDF
                    </>
                  )}
                </button>
            )}
          </div>
        </div>
      </div>

      {/* Content area with scrolling */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
          {/* Metadata — only show when generation is complete (during streaming, word count
              is still climbing and "generated date" is misleading because it's the start time). */}
          {!isStreaming && (
            <div className="text-sm text-muted-foreground space-y-1">
              <p>Generated: {new Date(codebook.created_at).toLocaleDateString()}</p>
              <p>Reading time: {Math.max(1, Math.ceil(codebook.word_count / 200))} min</p>
            </div>
          )}

          {/* Content blocks */}
          {codebook.blocks.length > 0 ? (
            <div className="space-y-8">
              {codebook.blocks.map((block, idx) => {
                // Per-block validation: code blocks become runnable as soon as the
                // backend marks them metadata.validated=true. For older codebooks
                // that don't have metadata.validated (pre-Option-A builds), fall
                // back to the old global rule (readOnly while streaming).
                const blockValidated = block.metadata?.validated === true;
                const blockReadOnly = isStreaming && !blockValidated;
                return (
                <div key={block.artifact_id || idx}>
                  {isNarrativeBlock(block) ? (
                    <CodebookNarrativeBlock content={block.content.markdown} />
                  ) : isCodeDualBlock(block) ? (
                    <CodebookDualCodeBlock
                      language_primary={block.content.primary.language}
                      code_primary={block.content.primary.code}
                      language_secondary={block.content.secondary.language}
                      code_secondary={block.content.secondary.code}
                      comparison_notes={block.content.comparison_notes}
                      codebookId={codebook.codebook_id}
                      blockIndex={idx}
                      readOnly={blockReadOnly}
                    />
                  ) : isCodeBlock(block) ? (
                    <CodebookCodeBlock
                      language={block.content.language}
                      code={block.content.code}
                      explanation={block.content.explanation}
                      codebookId={codebook.codebook_id}
                      blockIndex={idx}
                      readOnly={blockReadOnly}
                      setupSql={block.content.setup_sql}
                    />
                  ) : isNextTopicsBlock(block) && pathContext ? (
                    <div className="border border-purple-300 dark:border-purple-700 rounded-xl p-6 bg-purple-50/50 dark:bg-purple-900/20">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xl">🧭</span>
                        <h3 className="text-base font-semibold text-foreground">Step complete?</h3>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">
                        This tutorial is a step in your learning path. Head back to see what comes next on your route.
                      </p>
                      <button
                        onClick={() => navigate(`/paths/${pathContext.pathId}`)}
                        className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-blue-500 text-white text-sm font-semibold shadow-lg hover:from-purple-600 hover:to-blue-600 transition-all"
                      >
                        ← Back to your path
                      </button>
                    </div>
                  ) : isNextTopicsBlock(block) && onGenerateNext ? (
                    <div className="border border-border rounded-xl p-6 bg-card/50">
                      <h3 className="text-base font-semibold text-foreground mb-1">What's Next?</h3>
                      <p className="text-sm text-muted-foreground mb-4">Continue your learning journey with these follow-up tutorials.</p>
                      <div className="space-y-2">
                        {(block.content as { topics: string[] }).topics.map((topic, tIdx) => (
                          <button
                            key={tIdx}
                            onClick={() => onGenerateNext(topic)}
                            className="w-full text-left px-4 py-3 text-sm text-foreground bg-background hover:bg-primary/10 border border-border/50 rounded-lg transition-all hover:border-primary/40 flex items-center justify-between group"
                          >
                            <span>{topic}</span>
                            <svg className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
                );
              })}
              {/* Live "writing next section" indicator — only while streaming. */}
              {isStreaming && (
                <div className="border border-dashed border-primary/40 rounded-xl p-5 bg-primary/5 flex items-center gap-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-primary animate-pulse" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 rounded-full bg-primary animate-pulse" style={{ animationDelay: '200ms' }}></span>
                    <span className="w-2 h-2 rounded-full bg-primary animate-pulse" style={{ animationDelay: '400ms' }}></span>
                  </div>
                  <span className="text-sm text-foreground/80">Writing next section…</span>
                </div>
              )}
            </div>
          ) : isStreaming ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center gap-3 text-muted-foreground">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent"></div>
                <span>Preparing your tutorial…</span>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No content available</p>
            </div>
          )}

          {/* Footer spacing */}
          <div className="h-12" />
        </div>
      </div>

      {/* Documentation Modal */}
      <DocumentationModal
        isOpen={documentationOpen}
        onClose={() => setDocumentationOpen(false)}
        onPromptClick={handleDocumentationPromptClick}
        currentRoute="bookmate"
      />
    </div>
  );
};

export default CodebookViewer;
